import React, { Fragment, useState } from 'react';
import { useTranslate } from 'react-admin';
import lodashGet from 'lodash/get';
import lodashSet from 'lodash/set';

import { Drawer, EditButton } from '~/components';
import { EditForm } from '~/components/ra';
import { l } from '~/resources/pricings/PricingShow';
import { ByFeature } from '.';
import RegularFeeForm from './Forms/MembershipFeeForm';
import FeeForm from './Forms/FeeForm';
import AcquiringFeeForm from './Forms/AcquiringFeeForm';
import RegularFeeConfirmationField from './ConfirmationDrawers/RegularFeeConfirmationField';
import FeeConfirmationField from './ConfirmationDrawers/FeeConfirmationField';
import AcquiringFeeConfirmationField from './ConfirmationDrawers/AcquiringFeeConfirmationField';
import { Pricing } from '~/types';
import PricingInfo from './PricingInfo';
import FeeLoyaltyForm from './Forms/FeeLoyaltyForm';
import FeeLoyaltyConfirmationField from './ConfirmationDrawers/FeeLoyaltyConfirmationField';
import DefaultAmountLimitForm from './Forms/DefaultAmountLimitForm';
import DefaultCountLimitForm from './Forms/DefaultCountLimitForm';
import DefaultAmountLimitConfirmationField from './ConfirmationDrawers/DefaultAmountLimitConfirmationField';
import DefaultCountLimitConfirmationField from './ConfirmationDrawers/DefaultCountLimitConfirmationField';
import { NumberInput } from 'react-admin';
import { required } from 'react-admin';
import ForkInfo from './ForkInfo';
import { isEmpty } from 'lodash';

export interface PricingDataEditProps {
  schema: ByFeature;
  pricingRecord: Pricing;
  record: any;
  setMainState: (newState: any) => void;
  currency: string;
}

export const buildGetSource = (sourcePrefix: string) => (source: string) =>
  `${sourcePrefix}${source && `.${source}`}`;

const PricingDataEdit = (props: PricingDataEditProps) => {
  const { schema, record, setMainState, pricingRecord, currency } = props;

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key), 1);

  const getItemSource = buildGetSource(`${schema.propertyKey}`);
  const currentEntity = lodashGet(record, getItemSource(''));

  const handleSubmit = (data: any, submitQuery?: (a: any) => any) => {
    const cleanup = (record: any, source: string) => {
      const scopedRecord = lodashGet(record, source);
      if (typeof scopedRecord === 'object') {
        const { period, billingDateType, type, discountType } = scopedRecord;

        if (
          (schema.type === 'veengu_fee_default' ||
            schema.type === 'veengu_tax_default' ||
            schema.type === 'veengu_fee_acquiring') &&
          !type
        ) {
          return lodashSet(
            record,
            source,
            scopedRecord.tags
              ? {
                  tags: scopedRecord.tags,
                  index: scopedRecord.index,
                }
              : null
          );
        }

        let result = { ...scopedRecord };

        const isValidAmount = (a: any) =>
          a != null &&
          typeof a.value === 'number' &&
          typeof a.currency === 'string' &&
          a.currency.length > 0;

        // Remove fields based on billingDateType and period
        if (billingDateType === 'sliding') delete result.billingDate;
        if (period === 'annually') delete result.payOnTheFirstCalendarMonth;

        // Helper functions for type-based cleanup (see usage below).
        // When adding a new fee type, keep this in sync with:
        //   1. handleTypeChange() in FeeForm.tsx / AcquiringFeeForm.tsx (add form-state cleanup on type change)
        //   2. The switch() case in FeeForm.tsx / AcquiringFeeForm.tsx (add the new type's UI fields)
        const cleanupFixedPlusPercent = () => {
          // Tiered related fields
          if (!isEmpty(result?.tiers)) delete result.tiers;
          // Counter related fields
          if (!isEmpty(result?.rules)) delete result.rules;
          delete result.defaultRule;
          delete result.dynamicTypeCounterReference;
        };

        const cleanupFixed = () => {
          delete result.minAmount;
          delete result.maxAmount;
          delete result.percent;
          // Tiered related fields
          if (!isEmpty(result?.tiers)) delete result.tiers;
          // Counter related fields
          if (!isEmpty(result?.rules)) delete result.rules;
          delete result.defaultRule;
          delete result.dynamicTypeCounterReference;
        };

        const cleanupPercent = () => {
          delete result.amount;
          if (!isValidAmount(result.maxAmount)) delete result.maxAmount;
          if (!isValidAmount(result.minAmount)) delete result.minAmount;
          // Tiered related fields
          if (!isEmpty(result?.tiers)) delete result.tiers;
          // Counter related fields
          if (!isEmpty(result?.rules)) delete result.rules;
          delete result.defaultRule;
          delete result.dynamicTypeCounterReference;
        };

        const cleanupTiered = () => {
          delete result.minAmount;
          delete result.maxAmount;
          delete result.percent;
          delete result.amount;
          // Counter related fields
          if (!isEmpty(result?.rules)) delete result.rules;
          delete result.defaultRule;
          delete result.dynamicTypeCounterReference;
        };

        const cleanupCounter = () => {
          delete result.minAmount;
          delete result.maxAmount;
          delete result.percent;
          delete result.amount;
          // Tiered related fields
          if (!isEmpty(result?.tiers)) delete result.tiers;
        };

        // Remove fields based on type
        if (type) {
          const typeLower = type.toLowerCase();
          if (typeLower.includes('fixedpluspercent')) cleanupFixedPlusPercent();
          else if (typeLower.includes('fixed')) cleanupFixed();
          else if (typeLower.includes('percent')) cleanupPercent();
          else if (typeLower.includes('tiered')) cleanupTiered();
          else if (typeLower.includes('counter')) cleanupCounter();
        }

        // Remove fields based on discountType
        if (discountType) {
          const discountLower = discountType.toLowerCase();
          if (discountLower.includes('full')) {
            delete result.discountPercent;
            delete result.discountAmount;
          }
          if (discountLower.includes('percent')) {
            delete result.discountAmount;
          }
          if (discountLower.includes('fixed')) {
            delete result.discountPercent;
          }
        }

        // Sanitize incomplete amount objects: remove any amount-type field that
        // does not have both a numeric value and a non-empty currency string.
        const amountKeys = ['amount', 'minAmount', 'maxAmount', 'discountAmount'] as const;
        amountKeys.forEach((key) => {
          if (key in result && !isValidAmount(result[key])) delete result[key];
        });

        if (Array.isArray(result.tiers)) {
          result.tiers = result.tiers.map((tier: any) => {
            const tierCopy = { ...tier };
            (['amount', 'minAmount', 'maxAmount'] as const).forEach((key) => {
              if (key in tierCopy && !isValidAmount(tierCopy[key])) delete tierCopy[key];
            });
            return tierCopy;
          });
        }

        if (Array.isArray(result.rules)) {
          result.rules = result.rules.map((rule: any) => {
            const r = { ...rule };
            ([
              'minTransferAmount',
              'maxTransferAmount',
              'amount',
              'minAmount',
              'maxAmount',
            ] as const).forEach((key) => {
              if (key in r && !isValidAmount(r[key])) delete r[key];
            });
            return r;
          });
        }

        if (result.defaultRule != null) {
          const dr = { ...result.defaultRule };
          (['amount', 'minAmount', 'maxAmount'] as const).forEach((key) => {
            if (key in dr && !isValidAmount(dr[key])) delete dr[key];
          });
          result.defaultRule = dr;
        }

        return lodashSet(record, source, result);
      }
      return record;
    };
    const newData = cleanup({ ...record, ...data }, getItemSource(''));

    setMainState(newData);
    if (submitQuery) {
      submitQuery({
        ...pricingRecord,
        data: newData,
      });
    }
  };

  const handleDelete = (deleteQuery: () => void, submitQuery?: (a: any) => any) => {
    const newData = { ...record };
    if (!schema.propertyKey.startsWith('serviceCharges.')) {
      delete newData[schema.propertyKey];
    } else {
      newData.serviceCharges = {
        ...newData['serviceCharges'],
        [schema.propertyKey.substring('serviceCharges.'.length)]: null,
      };
    }

    setMainState(newData);
    if (submitQuery) {
      submitQuery({
        ...pricingRecord,
        data: newData,
      });
    }
  };

  return (
    <Fragment>
      <EditButton onClick={handleOpen} style={editButtonStyles} />
      <Drawer heading={t('editFee')} open={open} onClose={handleClose}>
        <PricingInfo schema={schema} />
        {currentEntity?.tags && <ForkInfo entity={currentEntity} />}
        <EditForm<any>
          record={record}
          resource='pricings'
          id={pricingRecord.id}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          withSaveConfirmation
          withDelete={currentEntity?.tags && currentEntity.tags.length > 0}
          saveConfirmationSettings={{
            variant: 'drawer',
            drawer: {
              heading: translate('utils.confirmConfigurationChanges'),
              content: (prevRecord, record) => {
                const oldEntity = lodashGet(prevRecord, getItemSource('')) || {};
                const newEntity = lodashGet(record, getItemSource('')) || {};
                return (
                  <Fragment>
                    <PricingInfo schema={schema} />
                    {oldEntity?.tags && <ForkInfo oldEntity={oldEntity} entity={newEntity} />}
                    {(function () {
                      switch (schema.type) {
                        case 'veengu_fee_membership':
                          return (
                            <RegularFeeConfirmationField
                              prevRecord={oldEntity}
                              record={newEntity}
                            />
                          );
                        case 'veengu_fee_default':
                          return <FeeConfirmationField prevRecord={oldEntity} record={newEntity} />;
                        case 'veengu_tax_default':
                          return (
                            <FeeConfirmationField prevRecord={oldEntity} record={newEntity} tax />
                          );
                        case 'veengu_fee_acquiring':
                          return (
                            <AcquiringFeeConfirmationField
                              prevRecord={oldEntity}
                              record={newEntity}
                            />
                          );
                        case 'veengu_service_charges':
                          return <FeeConfirmationField prevRecord={oldEntity} record={newEntity} />;
                        case 'veengu_fee_loyalty_default':
                          return (
                            <FeeLoyaltyConfirmationField
                              prevRecord={oldEntity}
                              record={newEntity}
                            />
                          );
                        case 'veengu_limit_default_amount':
                        case 'veengu_limit_single_operation_amount':
                          return (
                            <DefaultAmountLimitConfirmationField
                              prevRecord={oldEntity}
                              record={newEntity}
                            />
                          );
                        case 'veengu_limit_default_count':
                          return (
                            <DefaultCountLimitConfirmationField
                              prevRecord={oldEntity}
                              record={newEntity}
                            />
                          );
                        default:
                          return null;
                      }
                    })()}
                  </Fragment>
                );
              },
            },
          }}
        >
          {(function () {
            switch (schema.type) {
              case 'veengu_fee_membership':
                return (
                  <RegularFeeForm
                    currency={currency}
                    getSource={getItemSource}
                    prevRecord={record}
                  />
                );
              case 'veengu_fee_default':
                return (
                  <FeeForm
                    currency={currency}
                    getSource={getItemSource}
                    schema={schema}
                    format={pricingRecord.format}
                  />
                );
              case 'veengu_tax_default':
                return (
                  <FeeForm
                    currency={currency}
                    getSource={getItemSource}
                    schema={schema}
                    format={pricingRecord.format}
                    tax
                  />
                );
              case 'veengu_fee_acquiring':
                return (
                  <AcquiringFeeForm currency={currency} getSource={getItemSource} schema={schema} />
                );
              case 'veengu_service_charges':
                return (
                  <FeeForm
                    currency={currency}
                    getSource={getItemSource}
                    schema={schema}
                    format={pricingRecord.format}
                  />
                );
              case 'veengu_fee_loyalty_default':
                return (
                  <FeeLoyaltyForm currency={currency} getSource={getItemSource} schema={schema} />
                );
              case 'veengu_limit_default_amount':
              case 'veengu_limit_single_operation_amount': {
                const defaultValue = schema['v:default'];
                return (
                  <DefaultAmountLimitForm
                    currency={currency}
                    getSource={getItemSource}
                    defaultValue={defaultValue}
                  />
                );
              }
              case 'veengu_limit_default_count': {
                const defaultValue = schema['v:default'];
                return (
                  <DefaultCountLimitForm getSource={getItemSource} defaultValue={defaultValue} />
                );
              }
              default:
                return null;
            }
          })()}
          {currentEntity?.tags && (
            <NumberInput
              label={t('index')}
              source={getItemSource('index')}
              style={{ width: '100%' }}
              validate={[required()]}
            />
          )}
        </EditForm>
      </Drawer>
    </Fragment>
  );
};

const editButtonStyles = { marginTop: -6 };

export default PricingDataEdit;
